import Box from '@/common/components/Box';
import { ImageCarousel } from '@/common/components/ImageCarousel';
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder';
import SmallBadge from '@/common/components/SmallBadge';
import { formatUnit } from '@/common/utils/formatters';
import { ProposalModificationInput, useProposalModification } from '@/features/maintenance/features/establishmentChoice/features/auctions/hooks/useProposalModification';
import { DiagnosticPieceData } from '@/features/maintenance/features/workflows/types/forwardings/directiveEntries/diagnosticEntry/diagnosticEntry.type';
import { WrenchIcon } from 'lucide-react';
import { Fragment } from 'react';
import ProposalAwareValuesRow from '../../../../CreateExecutionAuctionProposal/components/ObjectiveAttributes/ProposalAwareValuesRow';

interface PieceRowProps {
  executionPiece: DiagnosticPieceData;
  proposalModification?: ProposalModificationInput;
}

const PieceRow = ({
  executionPiece,
  proposalModification,
}: PieceRowProps) => {
  const modification = useProposalModification({
    modification: proposalModification,
    unitPrice: executionPiece.unit_price,
    warrantyInDays: executionPiece.warranty_in_days,
    quantity: executionPiece.quantity,
    totalValue: executionPiece.total_value,
    disabled: true,
  })

  const quantityDisplay = (
    <SmallBadge
      text={formatUnit(executionPiece.quantity, 'item')}
    />
  )

  return (
    <Box className="p-2 rounded-lg">
      <div className="flex w-full items-start gap-4">
        <ImageCarousel
          files={executionPiece.piece?.images?.map((image) => ({ id: image?.url, file: image?.url })) || []}
          Placeholder={
            <ImageWithPlaceholder className="size-20 rounded-full" iconClassName="size-10" placeholderIcon={WrenchIcon} />
          }
          className="size-20"
          showExpandButton
          showRemoveButton={false}
        />

        <div className="flex flex-col gap-1 flex-1">
          <div className="flex items-center gap-3">
            <ProposalAwareValuesRow
              originalUnitPrice={modification.originalUnitPrice}
              displayNewUnitPrice={modification.displayNewUnitPrice}
              unitPricePercentage={modification.unitPricePercentage}
              originalTotalValue={modification.originalTotalValue}
              displayNewTotalValue={modification.displayNewTotalValue}
              totalValuePercentage={modification.totalValuePercentage}
              originalWarranty={modification.originalWarranty}
              displayNewWarranty={modification.displayNewWarranty}
              quantityDisplay={quantityDisplay}
            />
          </div>

          <p className="font-bold text-base">
            {executionPiece?.piece?.display_name || '--'}
          </p>

          <div className="flex items-center gap-1 flex-wrap">
            {[
              executionPiece?.piece?.number,
              executionPiece?.piece?.brand?.display_name,
              executionPiece?.piece?.global_trade_item_number,
            ]
              .filter(Boolean)
              .map((value, index) => (
                <Fragment key={index}>
                  {index > 0 && (
                    <p className="text-xs">
                      |
                    </p>
                  )}

                  <p className="text-xs">
                    {value}
                  </p>
                </Fragment>
              ))}
          </div>

          <p className="text-xs">
            {executionPiece.description}
          </p>
        </div>
      </div>
    </Box>
  )
}

export default PieceRow
